import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { z, ZodError } from "zod";
import {
	EmailRequest,
	EmailResponse,
	EmailResponseSchema,
	InitResponse,
	InitResponseSchema,
	OpenResponse,
	OpenResponseSchema,
	PdfRequest,
	PdfResponse,
	PdfResponseSchema,
	SaveRequest,
	SaveResponse,
	SaveResponseSchema,
} from "./schemas";

export const BASE_URL = process.env.API_URL;
export type ApiError = AxiosError | ZodError;

const buildUrl = (path: string, params: Record<string, string>): string => {
	const query = new URLSearchParams(params).toString();
	return `${BASE_URL}/${path}?${query}`;
};

const pricelistParam = (pricelistId: string | null): Record<string, string> =>
	// eslint-disable-next-line camelcase -- matches the API query key
	pricelistId ? { pricelist_id: pricelistId } : {};

type ZodTypedFetchPromise<T> = Promise<z.infer<T> | ApiError | undefined>;

const defaultParams: Record<string, string> = {
	company: "grythyttan-stalmobler",
};

const fetchAndParse = async <T extends z.ZodType>(
	url: string,
	schema: T,
	options?: AxiosRequestConfig,
): ZodTypedFetchPromise<T> => {
	const response = await axios({
		url,
		method: options?.method || "GET",
		...options,
		params: { ...defaultParams, ...options?.params },
	});

	const parsed = schema.safeParse(response.data);

	if (!parsed.success) {
		throw parsed.error;
	}

	return parsed.data;
};

// Usage examples:
const post = <T extends z.ZodType>(
	url: string,
	schema: T,
	body: unknown,
	params?: Record<string, string>,
): ZodTypedFetchPromise<T> =>
	fetchAndParse(url, schema, {
		method: "POST",
		data: body,
		headers: { Accept: "application/json" },
		params,
	});

const get = <T extends z.ZodType>(
	url: string,
	schema: T,
	params?: Record<string, string>,
): ZodTypedFetchPromise<T> => fetchAndParse(url, schema, { params });

export async function initRequest(
	guid: string,
	pricelistId: string | null,
	vatOverride: string | null,
	lan: string,
	countrycode: string | null,
): ZodTypedFetchPromise<InitResponse> {
	return await get(
		buildUrl("init", {
			guid,
			...pricelistParam(pricelistId),
			...(vatOverride && { vat: vatOverride }),
			lan,
			...(countrycode && { countrycode }),
		}),
		InitResponseSchema,
	);
}

export async function openRequest(
	guid: string,
	pricelistId: string | null,
	id: string,
): ZodTypedFetchPromise<OpenResponse> {
	return await get(
		buildUrl("open", {
			id,
			guid,
			...pricelistParam(pricelistId),
		}),
		OpenResponseSchema,
	);
}

export async function saveRequest(
	guid: string,
	pricelistId: string | null,
	body: SaveRequest,
): ZodTypedFetchPromise<SaveResponse> {
	return await post(
		buildUrl("save", {
			guid,
			...pricelistParam(pricelistId),
		}),
		SaveResponseSchema,
		body,
	);
}

export async function emailRequest(
	guid: string,
	pricelistId: string | null,
	body: EmailRequest,
): ZodTypedFetchPromise<EmailResponse> {
	return await post(
		buildUrl("email", {
			guid,
			...pricelistParam(pricelistId),
		}),
		EmailResponseSchema,
		body,
	);
}

export async function pdfRequest(
	guid: string,
	pricelistId: string | null,
	lan: string,
	countrycode: string | null,
	body: PdfRequest,
): ZodTypedFetchPromise<PdfResponse> {
	return await post(
		buildUrl("pdf", {
			guid,
			...pricelistParam(pricelistId),
			lan,
			...(countrycode && { countrycode }),
		}),
		PdfResponseSchema,
		body,
	);
}
